import { Component, OnInit, Input, EventEmitter, Output } from '@angular/core';
import { ViewEncapsulation } from '@angular/core';
import { asyncScheduler } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

import { AutoSub, AutoSubOnDestroy } from '../../providers';
import { GroupState, GroupStateMap, ResourceState } from '../../models';
import { OfflineService } from '../offline.service';

@AutoSubOnDestroy()
@Component({
    selector: 'offline',
    templateUrl: './offline.component.html',
    styleUrls: ['./offline.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class OfflineComponent implements OnInit {
    @Input() projectKey: string = "";
    @Input() showWelcome: boolean = true;

    @Output() stateChange = new EventEmitter<GroupState>();

    public showProgress: boolean = false;
    public progressMsg: string = "Please wait, downloading...";
    public progressPct: number = 0;
    public progressBytes: number = 0;

    public errMsg = "";
    public cacheStatus = "";
    public cacheMap: GroupStateMap = {};
    public resourceErrors: ResourceState[] = [];

    constructor(public offlineService: OfflineService) {
    }

    ngOnInit() {
        this.offlineService.awaitInit().then(async () => {
            // Check initial offline mode of active project
            AutoSub(this).reg['cacheProgress'] = this.offlineService.watchDownloadProgress().subscribe((progress) => {
                //console.log("Component got progress ", progress.totalPct);
                this.progressPct = progress.totalPct;
                this.progressMsg = progress.msg;
                this.progressBytes = progress.totalBytes;
                this.showProgress = (this.progressPct >= 100) ? false : true;
                // Enable offline mode when caching is done
                let cacheMap = this.cacheMap[this.projectKey];
                if(this.progressPct >= 100) {
                    console.log("Caching done, status: " + cacheMap.status);
                    console.log("Failed resources during download " + (this.resourceErrors.length) + " ", this.resourceErrors);
                }
                if(this.progressPct >= 100 && cacheMap && cacheMap.status == 'Cached') this.offlineService.enableOfflineMode(true);
            });

            AutoSub(this).reg['cacheErrors'] = this.offlineService.watchDownloadErrors().subscribe((resource) => {
                console.log("Registered download error ", resource);
                this.resourceErrors.push(resource);
            });

            AutoSub(this).reg['cacheMap'] = this.offlineService.watchCachingMap().pipe(throttleTime(300, asyncScheduler, { leading: true, trailing: true })).subscribe((cacheMap) => {
                this.cacheMap = cacheMap;
                this.updateCacheState(cacheMap);
            }, err => {
                console.warn("Offline caching failed: " + err);
                this.errMsg = err && err.msg ? err.msg : "Aborted, sorry can't enable offline mode on this device (unknown error)";
                this.updateCacheState(err.lastStateMap);
            });
        });
    }

    private updateCacheState(cacheMap: GroupStateMap) {
        let cacheState = cacheMap[this.projectKey];
        this.stateChange.emit(cacheState ? cacheState : new GroupState());

        if(!cacheState || !this.projectKey) return console.log(`Cache state for active project: '${this.projectKey}' uninitialized`);
        this.cacheStatus = cacheState ? cacheState.status : '';
    }

    public async requestOfflineMode(enable: boolean) {
        await this.offlineService.awaitInit();
        this.showProgress = enable;
        // Get offline cache of active project or delete it
        this.offlineService.enableOfflineCache(this.projectKey, enable);
        //  Disable offline mode
        if(!enable) this.offlineService.enableOfflineMode(false);
    }

    public resumeDownload(resourceErrors: ResourceState[]) {
        console.log("Resource errors", resourceErrors);
        this.showProgress = true;
        this.offlineService.resumeDownload(this.projectKey, resourceErrors);
        this.resourceErrors = [];
    }
}
